import { useEffect, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { format, useFormatBytes, useT } from '@/shared/i18n'
import { getUtilityDefinition } from '@/shared/utilities/utilities'
import {
  MAX_FILE_MB,
  type PdfMetadataFields,
  type PdfMetadataSaveResult,
  type PdfMetadataSnapshot,
  metadataFilename,
  readPdfMetadata,
  savePdfMetadata,
  validateFileSize,
} from './lib'
import { FilePickPrompt } from '@/shared/ui/FilePickPrompt'
import { RevealOnMount } from '@/shared/ui/RevealOnMount'

const UTILITY = getUtilityDefinition('pdf-metadata').analyticsKey

type Phase =
  | { kind: 'idle' }
  | { kind: 'loading'; file: File; action: 'read' | 'write' }
  | { kind: 'editing'; file: File; metadata: PdfMetadataSnapshot; fields: PdfMetadataFields }
  | {
      kind: 'success'
      file: File
      result: PdfMetadataSaveResult
      objectUrl: string
    }
  | { kind: 'error'; file: File | null; message: string; errorCode: string }

interface DropZoneProps {
  onFile: (file: File) => void
  disabled: boolean
}

function DropZone({ onFile, disabled }: DropZoneProps) {
  const t = useT()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    const file = files[0]
    if (!file) return
    if (!file.name.toLowerCase().endsWith('.pdf')) return
    onFile(file)
  }

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={t.common.pickPdf}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          inputRef.current?.click()
        }
      }}
      onDragOver={(e) => {
        e.preventDefault()
        if (!disabled) setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragging(false)
        if (!disabled) handleFiles(e.dataTransfer.files)
      }}
      className={cn(
        'flex cursor-pointer select-none flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed p-6 sm:p-10 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-colors',
        dragging && !disabled
          ? 'border-primary bg-primary/5'
          : 'border-border hover:border-primary/50 hover:bg-accent/30',
        disabled && 'pointer-events-none opacity-50',
      )}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-primary/60"
        aria-hidden="true"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <line x1="10" y1="9" x2="8" y2="9" />
      </svg>

      <div>
        <p className="text-sm font-medium text-foreground">
          <FilePickPrompt drag={t.common.dragPdf} />
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {format(t.common.pdfLimit, { size: MAX_FILE_MB })}
        </p>
        <p className="mt-1 flex items-center justify-center gap-1 text-[11px] text-muted-foreground/80">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          {t.common.localBadge}
        </p>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf"
        className="sr-only"
        tabIndex={-1}
        onChange={(e) => handleFiles(e.target.files)}
        disabled={disabled}
      />
    </div>
  )
}

interface FileBadgeProps {
  file: File
  onClear: () => void
  disabled: boolean
}

function FileBadge({ file, onClear, disabled }: FileBadgeProps) {
  const t = useT()
  const formatBytes = useFormatBytes()

  return (
    <div className="flex items-center gap-3 rounded-lg border bg-accent/30 px-4 py-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-xs font-semibold text-primary">
        PDF
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{file.name}</p>
        <p className="text-xs text-muted-foreground">{formatBytes(file.size)}</p>
      </div>
      <button
        type="button"
        onClick={onClear}
        disabled={disabled}
        aria-label={t.common.deleteFile}
        className={cn(
          'shrink-0 rounded p-1 text-muted-foreground transition-colors',
          'hover:bg-destructive/10 hover:text-destructive',
          disabled && 'pointer-events-none opacity-50',
        )}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  )
}

interface MetadataEditorProps {
  fields: PdfMetadataFields
  metadata: PdfMetadataSnapshot
  disabled: boolean
  onChange: (fields: PdfMetadataFields) => void
}

function MetadataEditor({ fields, metadata, disabled, onChange }: MetadataEditorProps) {
  const t = useT()

  function updateField<TKey extends keyof PdfMetadataFields>(
    key: TKey,
    value: PdfMetadataFields[TKey],
  ) {
    onChange({ ...fields, [key]: value })
  }

  return (
    <div className="space-y-5">
      <MetadataSummary fields={fields} metadata={metadata} />

      <div className="grid gap-4 md:grid-cols-2">
        <TextField
          label={t.pdfMetadata.titleLabel}
          value={fields.title}
          disabled={disabled}
          onChange={(value) => updateField('title', value)}
        />
        <TextField
          label={t.pdfMetadata.authorLabel}
          value={fields.author}
          disabled={disabled}
          onChange={(value) => updateField('author', value)}
        />
        <TextField
          label={t.pdfMetadata.subjectLabel}
          value={fields.subject}
          disabled={disabled}
          onChange={(value) => updateField('subject', value)}
          className="md:col-span-2"
        />
        <TextField
          label={t.pdfMetadata.keywordsLabel}
          value={fields.keywords}
          disabled={disabled}
          onChange={(value) => updateField('keywords', value)}
          placeholder={t.pdfMetadata.keywordsPlaceholder}
          className="md:col-span-2"
        />
        <DateField
          label={t.pdfMetadata.creationDateLabel}
          value={fields.creationDate}
          disabled={disabled}
          onChange={(value) => updateField('creationDate', value)}
        />
        <DateField
          label={t.pdfMetadata.modificationDateLabel}
          value={fields.modificationDate}
          disabled={disabled}
          onChange={(value) => updateField('modificationDate', value)}
        />
      </div>
    </div>
  )
}

interface MetadataSummaryProps {
  fields: PdfMetadataFields
  metadata: PdfMetadataSnapshot
}

function MetadataSummary({ fields, metadata }: MetadataSummaryProps) {
  const t = useT()
  const rows = [
    { label: t.pdfMetadata.titleLabel, value: fields.title },
    { label: t.pdfMetadata.authorLabel, value: fields.author },
    { label: t.pdfMetadata.creatorLabel, value: metadata.creator },
    { label: t.pdfMetadata.producerLabel, value: metadata.producer },
  ].filter((row) => row.value.trim() !== '')

  return (
    <Card className="bg-accent/20">
      <CardContent className="space-y-1.5 p-4 text-sm">
        <RevealOnMount />
        <p className="font-medium text-foreground">
          {format(t.pdfMetadata.summaryTitle, { pages: metadata.totalPages })}
        </p>
        {rows.length === 0 && (
          <p className="text-muted-foreground">{t.pdfMetadata.summaryEmpty}</p>
        )}
        {rows.map((row) => (
          <div key={row.label} className="flex justify-between gap-3">
            <span className="shrink-0 text-muted-foreground">{row.label}</span>
            <span className="min-w-0 break-words text-right font-medium text-foreground">
              {row.value}
            </span>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

interface TextFieldProps {
  label: string
  value: string
  disabled: boolean
  placeholder?: string
  className?: string
  onChange: (value: string) => void
}

function TextField({ label, value, disabled, placeholder, className, onChange }: TextFieldProps) {
  const t = useT()

  return (
    <label className={cn('block space-y-1.5', className)}>
      <span className="text-sm font-medium text-foreground">{label}</span>
      <input
        type="text"
        value={value}
        disabled={disabled}
        placeholder={placeholder ?? t.pdfMetadata.emptyPlaceholder}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          'h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm transition-colors',
          'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          disabled && 'cursor-not-allowed opacity-50',
        )}
      />
    </label>
  )
}

interface DateFieldProps {
  label: string
  value: string
  disabled: boolean
  onChange: (value: string) => void
}

function DateField({ label, value, disabled, onChange }: DateFieldProps) {
  return (
    <label className="block space-y-1.5">
      <span className="text-sm font-medium text-foreground">{label}</span>
      <input
        type="datetime-local"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          'h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          disabled && 'cursor-not-allowed opacity-50',
        )}
      />
    </label>
  )
}

interface UnsupportedMetadataNoticeProps {
  metadata: PdfMetadataSnapshot
}

function UnsupportedMetadataNotice({ metadata }: UnsupportedMetadataNoticeProps) {
  const t = useT()
  if (metadata.unsupportedInfoKeys.length === 0 && !metadata.hasXmpMetadata) return null

  return (
    <Card className="border-amber-300/70 bg-amber-50/70 dark:border-amber-500/40 dark:bg-amber-500/10">
      <CardContent className="space-y-2 p-4 text-sm">
        <p className="font-medium text-foreground">{t.pdfMetadata.unsupportedTitle}</p>
        <p className="text-muted-foreground">{t.pdfMetadata.unsupportedBody}</p>
        <div className="flex flex-wrap gap-2">
          {metadata.unsupportedInfoKeys.map((key) => (
            <span
              key={key}
              className="rounded-md border border-amber-300/70 bg-background px-2 py-1 text-xs text-foreground dark:border-amber-500/40"
            >
              {key}
            </span>
          ))}
          {metadata.hasXmpMetadata && (
            <span className="rounded-md border border-amber-300/70 bg-background px-2 py-1 text-xs text-foreground dark:border-amber-500/40">
              XMP
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

interface ResultPanelProps {
  file: File
  result: PdfMetadataSaveResult
  objectUrl: string
  onReset: () => void
}

function ResultPanel({ file, result, objectUrl, onReset }: ResultPanelProps) {
  const t = useT()
  const formatBytes = useFormatBytes()

  function handleDownload() {
    const a = document.createElement('a')
    a.href = objectUrl
    a.download = metadataFilename(file.name)
    a.click()
  }

  return (
    <Card className="border-green-200 bg-green-50/50 dark:border-green-500/30 dark:bg-green-500/10">
      <CardContent className="p-5">
        <RevealOnMount />
        <div className="mb-4 flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 text-green-600 dark:text-green-300"
            aria-hidden="true"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span className="text-base font-semibold text-green-700 dark:text-green-200">
            {t.common.done}
          </span>
        </div>

        <div className="mb-4 space-y-1.5 text-sm">
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.common.file}</span>
            <span className="truncate text-right font-medium text-foreground">{file.name}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.pdfMetadata.totalPages}</span>
            <span className="font-medium text-foreground">{result.totalPages}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.common.size}</span>
            <span className="font-medium text-foreground">{formatBytes(result.blob.size)}</span>
          </div>
        </div>

        {(result.unsupportedInfoKeys.length > 0 || result.hasXmpMetadata) && (
          <p className="mb-4 rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100">
            {t.pdfMetadata.unsupportedResultNotice}
          </p>
        )}

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Button onClick={handleDownload} size="lg" className="flex-1">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {t.pdfMetadata.downloadPdf}
          </Button>
          <Button variant="outline" onClick={onReset} className="flex-1 sm:flex-none">
            {t.pdfMetadata.again}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

interface ErrorPanelProps {
  message: string
  onRetry: () => void
}

function ErrorPanel({ message, onRetry }: ErrorPanelProps) {
  const t = useT()

  return (
    <Card className="border-destructive/30 bg-destructive/5">
      <CardContent className="p-5">
        <div className="mb-3 flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 text-destructive"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span className="text-sm font-semibold text-destructive">{t.common.errorTitle}</span>
        </div>
        <p className="mb-4 text-sm text-foreground">{message}</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t.common.retry}
        </Button>
      </CardContent>
    </Card>
  )
}

function Spinner() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="animate-spin"
      aria-hidden="true"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  )
}

export function MetadannyePdfForm() {
  const t = useT()
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' })

  useEffect(() => {
    return () => {
      if (phase.kind === 'success') {
        URL.revokeObjectURL(phase.objectUrl)
      }
    }
  }, [phase])

  const isLoading = phase.kind === 'loading'
  const currentFile =
    phase.kind === 'loading' ||
    phase.kind === 'editing' ||
    phase.kind === 'success' ||
    phase.kind === 'error'
      ? phase.file
      : null

  async function handleFile(file: File) {
    if (!validateFileSize(file)) {
      toolError(UTILITY, 'client_too_large')
      setPhase({
        kind: 'error',
        file,
        errorCode: 'client_too_large',
        message: format(t.common.tooLarge, { size: MAX_FILE_MB }),
      })
      return
    }

    revokeSuccessUrl()
    setPhase({ kind: 'loading', file, action: 'read' })
    toolInvoke(UTILITY, { action: 'read' })
    const startMs = Date.now()

    try {
      const metadata = await readPdfMetadata(file)
      toolSuccess(UTILITY, Date.now() - startMs, {
        action: 'read',
        total_pages: metadata.totalPages,
        unsupported_info_keys: metadata.unsupportedInfoKeys.length,
        has_xmp: metadata.hasXmpMetadata,
      })
      setPhase({ kind: 'editing', file, metadata, fields: metadata.fields })
    } catch (err: unknown) {
      handleProcessingError(file, err, 'read')
    }
  }

  function handleClear() {
    revokeSuccessUrl()
    setPhase({ kind: 'idle' })
  }

  function handleRetry() {
    if (phase.kind === 'error' && phase.file && phase.errorCode !== 'client_too_large') {
      void handleFile(phase.file)
    } else {
      setPhase({ kind: 'idle' })
    }
  }

  async function handleSubmit() {
    if (phase.kind !== 'editing') return

    const file = phase.file
    const fields = phase.fields
    setPhase({ kind: 'loading', file, action: 'write' })
    toolInvoke(UTILITY, { action: 'write' })
    const startMs = Date.now()

    try {
      const result = await savePdfMetadata(file, fields)
      const objectUrl = URL.createObjectURL(result.blob)
      toolSuccess(UTILITY, Date.now() - startMs, {
        action: 'write',
        total_pages: result.totalPages,
        output_bytes: result.blob.size,
        unsupported_info_keys: result.unsupportedInfoKeys.length,
        has_xmp: result.hasXmpMetadata,
      })
      setPhase({ kind: 'success', file, result, objectUrl })
    } catch (err: unknown) {
      handleProcessingError(file, err, 'write')
    }
  }

  function handleProcessingError(file: File, err: unknown, action: 'read' | 'write') {
    const message = err instanceof Error ? err.message.toLowerCase() : ''
    const code = message.includes('encrypted')
      ? 'encrypted'
      : message.includes('invalid_date')
        ? 'invalid_date'
        : 'parse_error'

    toolError(UTILITY, code, { action })
    setPhase({
      kind: 'error',
      file,
      errorCode: code,
      message:
        code === 'encrypted'
          ? t.pdfMetadata.encrypted
          : code === 'invalid_date'
            ? t.pdfMetadata.invalidDate
            : t.pdfMetadata.parseError,
    })
  }

  function revokeSuccessUrl() {
    if (phase.kind === 'success') {
      URL.revokeObjectURL(phase.objectUrl)
    }
  }

  if (phase.kind === 'success') {
    return (
      <ResultPanel
        file={phase.file}
        result={phase.result}
        objectUrl={phase.objectUrl}
        onReset={handleClear}
      />
    )
  }

  return (
    <div className="space-y-5">
      {currentFile ? (
        <FileBadge file={currentFile} onClear={handleClear} disabled={isLoading} />
      ) : (
        <DropZone onFile={(file) => void handleFile(file)} disabled={isLoading} />
      )}

      {phase.kind === 'loading' && (
        <Card className="bg-accent/20">
          <CardContent className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
            <Spinner />
            {phase.action === 'read' ? t.pdfMetadata.loading : t.pdfMetadata.saving}
          </CardContent>
        </Card>
      )}

      {phase.kind === 'editing' && (
        <MetadataEditor
          fields={phase.fields}
          metadata={phase.metadata}
          disabled={isLoading}
          onChange={(fields) => setPhase({ ...phase, fields })}
        />
      )}

      {phase.kind === 'error' && <ErrorPanel message={phase.message} onRetry={handleRetry} />}

      {(phase.kind === 'editing' || isLoading) && currentFile && (
        <Button
          size="lg"
          className="sticky bottom-3 z-10 w-full shadow-lg"
          disabled={phase.kind !== 'editing'}
          onClick={handleSubmit}
        >
          {phase.kind === 'loading' && phase.action === 'write' ? (
            <>
              <Spinner />
              {t.pdfMetadata.saving}
            </>
          ) : (
            t.pdfMetadata.save
          )}
        </Button>
      )}

      {phase.kind === 'editing' && (
        <>
          <UnsupportedMetadataNotice metadata={phase.metadata} />
          <p className="text-xs text-muted-foreground">{t.pdfMetadata.scopeBody}</p>
        </>
      )}
    </div>
  )
}
